import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { formatManwon } from '../../utils/formatKRW';
import { logEvent } from '../../utils/ga';
import Adsense from '../Ads/Adsense';
import { ExpertCallout } from '../shared/ExpertCallout';
import { TaxBenefitChart } from './Chart/TaxBenefitChart';
import { TaxBenefitForm } from './Form/TaxBenefitForm';
import {
  calculateTaxBenefit,
  getTaxBenefitCallout,
  TaxBenefitFormValues,
  TaxBenefitResult,
} from './TaxBenefitPage.hooks';
import styles from './TaxBenefitPage.module.css';

function TaxBenefitPage() {
  const navigate = useNavigate();
  const resultRef = useRef<HTMLDivElement>(null);
  const [result, setResult] = useState<TaxBenefitResult>();
  const [values, setValues] = useState<TaxBenefitFormValues>();

  const handleSubmit = useCallback((nextValues: TaxBenefitFormValues) => {
    logEvent('calculator', 'tax_benefit_calculate', 'submit');
    setValues(nextValues);
    setResult(calculateTaxBenefit(nextValues));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(
    () => (result && values ? getTaxBenefitCallout(result, values) : null),
    [result, values]
  );

  return (
    <div className={styles.root}>
      <Helmet>
        <title>ISA·IRP 세제혜택 계산기 — 절세 효과 확인 | 노후든든</title>
        <meta
          name="description"
          content="IRP·ISA 세제혜택 계산기. 연간 납입액으로 돌려받는 세액공제 금액과 ISA 비과세 절세 효과를 바로 확인하세요."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>ISA·IRP 세제혜택 계산기</h1>
          <p className={styles.pageDesc}>
            세액공제와 비과세 혜택을 합산해서, 매년 얼마나 확정 수익을 얻는지 확인하세요.
          </p>
        </div>
        <div className={styles.formCard}>
          <TaxBenefitForm onSubmit={handleSubmit} />
        </div>
      </Container>

      {result && values && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              <div className={styles.resultHeroCard}>
                <p className={styles.resultHeroLabel}>연간 절세 혜택 합계</p>
                <p className={styles.resultHeroAmount}>{formatManwon(result.annualTotalBenefit)}</p>
                <p className={styles.resultHeroSub}>
                  IRP 세액공제 + ISA 비과세 절세 · 적용 공제율 {(result.irpTaxCreditRate * 100).toFixed(1)}%
                </p>
              </div>

              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>IRP 세액공제</p>
                  <p className={styles.metricValue}>{formatManwon(result.irpAnnualBenefit)}</p>
                  <p className={styles.metricInterpretation}>올해 환급받는 세금</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>ISA 절세액</p>
                  <p className={styles.metricValue}>{formatManwon(result.isaAnnualTaxSaving)}</p>
                  <p className={styles.metricInterpretation}>5년 운용 기준 연간화</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>{values.projectionYears}년 누적 절세</p>
                  <p className={styles.metricValue}>{formatManwon(result.cumulativeBenefit)}</p>
                  <p className={styles.metricInterpretation}>같은 납입 지속 시</p>
                </div>
              </div>

              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>IRP·ISA 절세 효과 {values.projectionYears}년 누적</p>
                <div className={styles.chartWrapper}>
                  <TaxBenefitChart yearlyData={result.yearlyData} />
                </div>
              </div>

              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('engagement', 'tax_benefit_cta_click', '/compound');
                    navigate('/compound');
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  절세액 재투자 효과 계산하기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="TAX_BENEFIT_AD_SLOT" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
                이 계산기는 2024년 세법 기준입니다. 법 개정에 따라 내용이 변경될 수 있으며, 정확한 내용은 국세청 또는 세무사에게 확인하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default TaxBenefitPage;
